import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { syncedLocationKeys } from './synced-locations';
import type {
  BlockLocationRequest,
  BlockLocationResponse,
  UnblockLocationResponse,
  BatchBlockRequest,
  BatchBlockResponse,
  BatchUnblockRequest,
  BatchUnblockResponse,
} from '@/types/api';

interface BlockLocationParams {
  syncedLocationId: string;
  reason: string;
}

interface ErrorResponse {
  success: false;
  error: string;
}

/**
 * Hook to block a single synced location.
 * Blocked locations cannot be published.
 */
export function useBlockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ syncedLocationId, reason }: BlockLocationParams) => {
      const response = await api.post<BlockLocationResponse | ErrorResponse>(
        `/api/v1/synced-locations/${syncedLocationId}/block`,
        { reason } satisfies BlockLocationRequest
      );
      return response;
    },
    onSuccess: (_, { syncedLocationId }) => {
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

/**
 * Hook to unblock a single synced location.
 */
export function useUnblockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (syncedLocationId: string) => {
      const response = await api.post<UnblockLocationResponse | ErrorResponse>(
        `/api/v1/synced-locations/${syncedLocationId}/unblock`,
        {}
      );
      return response;
    },
    onSuccess: (_, syncedLocationId) => {
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

/**
 * Hook to batch block multiple synced locations.
 * Skips locations that are already blocked or published.
 */
export function useBatchBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationIds, reason }: BatchBlockRequest) => {
      const response = await api.post<BatchBlockResponse | ErrorResponse>(
        '/api/v1/synced-locations/batch-block',
        { locationIds, reason }
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

/**
 * Hook to batch unblock multiple synced locations.
 */
export function useBatchUnblock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationIds }: BatchUnblockRequest) => {
      const response = await api.post<BatchUnblockResponse>(
        '/api/v1/synced-locations/batch-unblock',
        { locationIds }
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}
